import { WeekPicker } from 'app/components/common/DatePickers';
import { AgenturNameCell } from 'app/components/shared/AgenturName.cell';
import { Table } from 'app/components/table/table';
import { AggregrationType, ITableColumn, TableModel, TableRow } from 'app/components/table/table.model';
import { useStore } from 'app/context';
import { AkumaAktionenVM } from 'app/stores/ui/agt.akumaaktionen.ui.store';
import { formatNum, formatPercentTwoDig } from 'app/utils';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { useEffect, useMemo } from 'react';
import { useHistoryWithQuery } from 'app/hooks/useHistoryWithQuery';
import _ from 'lodash';
import { Icon } from 'app/components/common/Icon';

export const AkumaAktionenOptions = observer(() => {
	const { agtAkumaAktionenUiStore } = useStore();

	const onBmChange = (value: number) => {
		runInAction(() => {
			agtAkumaAktionenUiStore.setKw(value);
		});
	};

	const bmOptions = agtAkumaAktionenUiStore.availableKws.map((bm) => ({
		value: bm,
		label: `${Math.floor(bm / 100)}/${bm % 100}`,
	}));

	return <WeekPicker value={agtAkumaAktionenUiStore.selectedKw} opts={bmOptions} onChange={onBmChange} emptyText="Alle Monate" queryParam="kw" />;
});

export const AkumaAktionenListe = observer(() => {
	const { berichteUiiStore, agtAkumaAktionenUiStore, agenturListUiStore } = useStore();
	const history = useHistoryWithQuery();

	const tm = useMemo(() => {
		const tm = new TableModel<AkumaAktionenVM, number>();
		const cols: ITableColumn<AkumaAktionenVM, number>[] = [];

		cols.push({
			label: 'Agentur',
			path: 'agt.name',
			sortBy: 'agt.name',
			render: AgenturNameCell,
		});

		cols.push({
			label: 'Aktionen',
			path: 'item.countCampaigns',
			sortBy: 'item.countCampaigns',
		});

		cols.push({
			label: 'MAZ',
			path: 'item.maz',
			format: formatNum,
			agg: [
				{
					path: 'item.maz',
					aggType: AggregrationType.sum,
				},
			],
		});

		cols.push({
			label: 'Email',
			path: 'item.email',
			format: formatNum,
			agg: [
				{
					path: 'item.email',
					aggType: AggregrationType.sum,
				},
			],
		});

		cols.push({
			label: 'Brief',
			path: 'item.brief',
			format: formatNum,
			agg: [
				{
					path: 'item.brief',
					aggType: AggregrationType.sum,
				},
			],
		});

		cols.push({
			label: 'Inbound',
			path: 'item.inbound',
			format: formatNum,
			agg: [
				{
					path: 'item.inbound',
					aggType: AggregrationType.sum,
				},
			],
		});

		cols.push({
			label: 'Outbound',
			path: 'item.outbound',
			format: formatNum,
			agg: [
				{
					path: 'item.outbound',
					aggType: AggregrationType.sum,
				},
			],
		});

		cols.push({
			label: 'Aktionstreffer',
			path: 'item.campaignCountCont',
			format: formatNum,
			agg: [
				{
					path: 'item.campaignCountCont',
					aggType: AggregrationType.sum,
				},
			],
		});

		// cols.push({
		// 	label: 'Agentur Tasks',
		// 	path: 'item.campaignCountAmistaskAgency',
		// 	format: formatNum,
		// 	agg: [
		// 		{
		// 			path: 'item.campaignCountAmistaskAgency',
		// 			aggType: AggregrationType.sum,
		// 		},
		// 	],
		// });

		// cols.push({
		// 	label: 'Mitarbeiter Tasks',
		// 	path: 'item.campaignCountAmistaskEmployee',
		// 	format: formatNum,
		// 	agg: [
		// 		{
		// 			path: 'item.campaignCountAmistaskEmployee',
		// 			aggType: AggregrationType.sum,
		// 		},
		// 	],
		// });

		cols.push({
			label: 'Bearbeitet',
			path: 'item.campaignCountTotal',
		});

		tm.onRowClick = (row: TableRow<AkumaAktionenVM>) => {
			history.pushWithQuery({
				search: '?agtId=' + row.data.agt.agtId,
			});
		};

		tm.sortBy = 'agt.name';
		tm.setCols(cols);

		tm.idProperty = 'agt.agtId';
		tm.idType = 'number';

		return tm;
	}, []);

	useEffect(() => {
		agtAkumaAktionenUiStore.loadAgtAktionsDataByKw(agtAkumaAktionenUiStore.selectedKw).then(() => {
			let items = agtAkumaAktionenUiStore.items;
			if (berichteUiiStore.selectedAgt) {
				const agtId = berichteUiiStore.selectedAgt.agtId;
				items = items.filter((i) => i.agt.agtId === agtId);
				items = _.sortBy(items, 'bm').reverse();
			} else {
				items = agtAkumaAktionenUiStore.applyFilter(items);
			}

			tm.setRowData(items);
			tm.sort();
		});
	}, [
		tm,
		berichteUiiStore.selectedAgt,
		agenturListUiStore.filterHasChanged,
		agenturListUiStore.currentFilter.filtersWithValue.length,
		agtAkumaAktionenUiStore.selectedKw,
		agtAkumaAktionenUiStore.items,
	]);

	return (
		<>
			<div className="table-container">
				<AkumaAktionenOptions />
				<Table stickyHeader={true} tm={tm} stickyFooter={true}></Table>
			</div>
		</>
	);
});
